import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { FormFieldErrorsState } from '../../shared/design-system/form-field-errors';
import { ParsianProviderSettings } from './payment-provider-settings.models';

@Component({
  standalone: true,
  selector: 'ain-parsian-provider-settings-form',
  imports: [FormsModule, InputTextModule, CheckboxModule, AinFormFieldComponent, InfoHintComponent],
  template: `
    <div class="parsian-settings">
      <ain-form-field
        [label]="t('payments.providers.gateway.parsian.merchantLoginAccount')"
        [errorKey]="fieldErrors.errorKey('merchantLoginAccount')">
        <input
          pInputText
          class="wide"
          [ngModel]="settings.merchantLoginAccount"
          (ngModelChange)="patch({ merchantLoginAccount: $event })"
          name="parsianMerchantLoginAccount"
          [placeholder]="t('payments.providers.gateway.parsian.merchantLoginAccountPlaceholder')"
          (blur)="touchField('merchantLoginAccount')" />
      </ain-form-field>

      <label class="checkbox-row">
        <p-checkbox
          [binary]="true"
          [ngModel]="settings.rialToToman"
          (ngModelChange)="patch({ rialToToman: $event })"
          name="parsianRialToToman" />
        <span>{{ t('payments.providers.gateway.parsian.rialToToman') }}</span>
        <ain-info-hint
          [titleKey]="'payments.providers.gateway.parsian.rialToTomanHint.title'"
          [descriptionKey]="'payments.providers.gateway.parsian.rialToTomanHint.description'" />
      </label>

      <label class="checkbox-row">
        <p-checkbox
          [binary]="true"
          [ngModel]="settings.blockOverseas"
          (ngModelChange)="patch({ blockOverseas: $event })"
          name="parsianBlockOverseas" />
        <span>{{ t('payments.providers.gateway.parsian.blockOverseas') }}</span>
        <ain-info-hint
          [titleKey]="'payments.providers.gateway.parsian.blockOverseasHint.title'"
          [descriptionKey]="'payments.providers.gateway.parsian.blockOverseasHint.description'" />
      </label>
    </div>
  `,
  styles: [`
    .parsian-settings { display: grid; gap: 12px; }
    .wide { width: 100%; }
    .checkbox-row {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: #334155;
    }
  `]
})
export class ParsianProviderSettingsFormComponent {
  private readonly localization = inject(LocalizationService);

  @Input({ required: true }) settings!: ParsianProviderSettings;
  @Input() fieldErrors = new FormFieldErrorsState();
  @Input() validateOnBlur = true;

  @Output() settingsChange = new EventEmitter<ParsianProviderSettings>();

  patch(partial: Partial<ParsianProviderSettings>): void {
    this.settingsChange.emit({ ...this.settings, ...partial });
    if (partial.merchantLoginAccount !== undefined) {
      this.fieldErrors.clear('merchantLoginAccount');
    }
  }

  touchField(field: string): void {
    if (!this.validateOnBlur || field !== 'merchantLoginAccount') {
      return;
    }
    this.validateMerchantField();
  }

  validateMerchantField(): boolean {
    const value = this.settings.merchantLoginAccount.trim();
    if (!value) {
      this.fieldErrors.setField('merchantLoginAccount', 'payments.providers.gateway.parsian.errors.merchantRequired');
      return false;
    }
    if (!/^[0-9a-fA-F-]{36}$/.test(value)) {
      this.fieldErrors.setField('merchantLoginAccount', 'payments.providers.gateway.parsian.errors.merchantInvalid');
      return false;
    }
    this.fieldErrors.clear('merchantLoginAccount');
    return true;
  }

  validateAll(): boolean {
    return this.validateMerchantField();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
